'use client';

import { useEffect, useMemo } from 'react';
import { AsyncIconFontLinks } from '@/components/AsyncIconFontLinks';
import { useAuthBoot } from '@/app/login/context/AuthBootContext';
import { FeatureFlags } from '@/lib/consts/featureFlags';
import {
    BASE_FONT_STYLESHEET_HREFS,
    SHARED_UI_HARDCODED_FONT_FAMILIES,
    resolveThemeFontStylesheets,
} from '@/lib/fonts/googleFonts';

function ensureStylesheet(href: string): void {
    if (document.querySelector(`link[rel="stylesheet"][href="${href}"]`)) {
        return;
    }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
}

/**
 * Injects the full store font set after hydration on both the login and shop
 * shells: the fonts.net base kit, theme + shared-ui Google families, Material
 * icon kits, and the self-hosted `@font-face` families from shared-ui's
 * fonts.css (BubblegumBliss, Gilroy, Font Awesome, …).
 *
 * Both critical paths ship a trimmed font set to keep fonts.googleapis.com /
 * fonts.net CSS off the render-blocking path (login ships only the
 * login/general family; shop ships none). The self-hosted families live only
 * in shared-ui's fonts.css, which login never imports because it excludes the
 * shared-ui barrel — that is why the publisher theme font renders as a fallback
 * on /login. This runs post-paint: it appends the missing kits and lazy-loads
 * the fonts.css chunk, so every family renders on both shells without blocking
 * first paint or pulling the shared-ui barrel. Existing links are deduped.
 */
export function EnsureStoreFonts(): React.ReactElement {
    const { authBoot } = useAuthBoot();
    const isConnectionLossEnabled = Boolean(
        authBoot.featureFlags?.[FeatureFlags.DASHBOARD_CONNECTION_LOSS_MODAL],
    );
    const connectionLoss = isConnectionLossEnabled
        ? authBoot.storeTheme?.storeSettings?.connectionLoss
        : undefined;

    const overlayFonts = useMemo(
        () =>
            [
                connectionLoss?.title?.font,
                connectionLoss?.subtitle?.font,
                connectionLoss?.button?.font,
            ].filter((font): font is string => Boolean(font)),
        [
            connectionLoss?.title?.font,
            connectionLoss?.subtitle?.font,
            connectionLoss?.button?.font,
        ],
    );

    const themeHrefs = useMemo(
        () =>
            resolveThemeFontStylesheets([
                authBoot.storeTheme?.general?.font,
                authBoot.storeTheme?.login?.font,
                authBoot.storeTheme?.support?.general?.fontFamily,
                authBoot.storeTheme?.support?.formFields?.fontFamily,
                ...overlayFonts,
                ...SHARED_UI_HARDCODED_FONT_FAMILIES,
            ]),
        [
            authBoot.storeTheme?.general?.font,
            authBoot.storeTheme?.login?.font,
            authBoot.storeTheme?.support?.general?.fontFamily,
            authBoot.storeTheme?.support?.formFields?.fontFamily,
            overlayFonts,
        ],
    );

    useEffect(() => {
        [...BASE_FONT_STYLESHEET_HREFS, ...themeHrefs].forEach(ensureStylesheet);
        void import('@/lib/fonts/localFontFaces');
    }, [themeHrefs]);

    return (
        <>
            <AsyncIconFontLinks />
            {/* Not `display: none` — a face nothing lays out is never downloaded. */}
            {overlayFonts.map((font) => (
                <span
                    key={font}
                    aria-hidden
                    style={{ position: 'absolute', visibility: 'hidden', fontFamily: font }}
                >
                    .
                </span>
            ))}
        </>
    );
}
